import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useFilters } from 'react-table';
import HoursDisplayReverse from '@/components/data/hours/HoursDisplayReversed';
import { professionalengagementsCss } from '@digital_monorepo/styling';

const DraftsHoursReport = ({ date, report }) => {
  const { t } = useTranslation();

  const columns = useMemo(() => [
    
    {
      Header: t('DRAFTS_TABLE.HEADERS.PROFESSIONAL_ID'),
      accessor: 'ProfessionalID',
      Cell: ({ value }) => <Link to={`/professionals/${value}`}>{value}</Link>,
      filter: 'text',
    },
    {
      Header: t('DRAFTS_TABLE.HEADERS.PROFESSIONAL_NAME'),
      accessor: 'ProfessionalName',
      filter: 'text',
    },
    {
      Header: t('DRAFTS_TABLE.HEADERS.PROFESSIONAL_FUNCTION'),
      accessor: 'FunctionName',
      filter: 'text',
    },
    {
      Header: t('DRAFTS_TABLE.HEADERS.ENGAGEMENT_ID'),
      accessor: 'EngagementID',
      Cell: ({ value }) => <Link to={`/engagements/${value}`}>{value}</Link>,
      filter: 'text',
    },
    {
      Header: t('DRAFTS_TABLE.HEADERS.ENGAGEMENT_NAME'),
      accessor: 'EngagementName',
      filter: 'text',
    },
    {
      Header: t('DRAFTS_TABLE.HEADERS.HOURS'),
      accessor: 'Hours',
      Cell: ({ value }) => <HoursDisplayReverse hours={value} freetime />,
    },    
  ], [t]);

  const data = useMemo(() => report, [report]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data }, useFilters, useSortBy);

  return (
    <div className={professionalengagementsCss.sectionContainer}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle}>
          {date}
        </h3>
      </div>
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={professionalengagementsCss.tableContainer}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...restHeaderGroupProps} className={professionalengagementsCss.headerRow}>
                    {headerGroup.headers.map((column) => {
                      const { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th
                          key={key}
                          {...restColumn}
                          className={professionalengagementsCss.th}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            {column.render('Header')}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? ' 🔽'
                                  : ' 🔼'
                                : ''}
                            </span>
                          </div>
                          {column.canFilter && (
                            <input
                              type="text"
                              value={column.filterValue || ''}
                              onChange={(e) => column.setFilter(e.target.value || undefined)}
                              placeholder={`Filter...`}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No draft hours match the current filters
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  const { key, ...restRowProps } = row.getRowProps();
                  return (
                    <tr key={key} {...restRowProps} className={professionalengagementsCss.bodyRow}>
                      {row.cells.map((cell) => {
                        const { key, ...restCellProps } = cell.getCellProps();
                        return (
                          <td
                            key={key}
                            {...restCellProps}
                            className={professionalengagementsCss.td}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

DraftsHoursReport.propTypes = {
  date: PropTypes.string.isRequired,
  report: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DraftsHoursReport;
